import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Background from './components/Background';
import SplashCursor from './components/SplashCursor';
import ProtectedRoute from './components/ProtectedRoute';
import PageTransition from './components/PageTransition';
import { LoadingProvider } from './context/LoadingContext';
import { AuthProvider } from './context/AuthContext';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import GalleryPage from './pages/GalleryPage';
import ContactPage from './pages/ContactPage';
import ReviewsPage from './pages/ReviewsPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminReviewsPage from './pages/AdminReviewsPage';
import AdminPhotosPage from './pages/AdminPhotosPage';
import NotFoundPage from './pages/NotFoundPage';

// Email service now uses API - no initialization needed
import './styles/App.css';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

debugLog('[DEBUG App] Module loaded');
// Auth is now handled via simple token-based authentication in AuthContext

const App: React.FC = () => {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const showHeaderFooter = location.pathname !== '/';
  
  
  // Add or remove landing-page-active class to body based on current route
  useEffect(() => {
    if (isLandingPage) {
      document.body.classList.add('landing-page-active');
    } else {
      document.body.classList.remove('landing-page-active');
    }
    
    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove('landing-page-active');
    };
  }, [isLandingPage]);
  
  return (
    <AuthProvider>
      <LoadingProvider>
        <div className="app">
          <Background />
          {isLandingPage && (
            <SplashCursor
              BACK_COLOR={{ r: 0.902, g: 0.794, b: 0.133 }}
              SPLAT_RADIUS={0.25}
              CURL={10}
              SPLAT_FORCE={8000}
              COLOR_UPDATE_SPEED={5}
            />
          )}
          {showHeaderFooter && <Header />}
          <main className={`main-content ${!showHeaderFooter ? 'full-height' : ''}`}>
            <PageTransition>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/reviews" element={<ReviewsPage />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route
                  path="/admin/reviews"
                  element={
                    <ProtectedRoute>
                      <AdminReviewsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/photos"
                  element={
                    <ProtectedRoute>
                      <AdminPhotosPage />
                    </ProtectedRoute>
                  }
                />

                {/* 404 catch-all — must be last */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </PageTransition>
          </main>
          {showHeaderFooter && <Footer />}
        </div>
      </LoadingProvider>
    </AuthProvider>
  );
};

export default App;